import { toast } from 'sonner'
import { supabase } from './supabase'
import { fetchWithRetry } from './request-manager'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

/**
 * Confirm booking with suppliers after payment
 * This triggers holds/prebooks and final confirmation with RateHawk/Duffel
 */
export async function confirmBooking(orderId: string) {
  try {
    // Step 1: Create holds/prebooks
    const holdsResponse = await apiFetch('/api/orchestration/holds', {
      method: 'POST',
      body: JSON.stringify({ orderId })
    })
    
    if (!holdsResponse.ok) {
      const error = await holdsResponse.json()
      throw new Error(error.error || 'Failed to create holds')
    }
    
    // Step 2: Confirm bookings
    const confirmResponse = await apiFetch('/api/orchestration/confirm', {
      method: 'POST',
      body: JSON.stringify({ orderId })
    })
    
    if (!confirmResponse.ok) {
      const error = await confirmResponse.json()
      throw new Error(error.error || 'Failed to confirm booking')
    }
    
    const result = await confirmResponse.json()
    return result
  } catch (error) {
    console.error('[confirmBooking] Error:', error)
    throw error
  }
}

// VOY-36: Enhanced apiFetch with retry, abort, and queue support
export async function apiFetch(
  path: string, 
  // suppressErrorToast (VOY-608): skip the generic toast with the raw response body on a
  // non-OK response, for callers that show their own error message.
  options: RequestInit & { abortKey?: string; suppressErrorToast?: boolean } = {}
) {
  const session = (await supabase.auth.getSession()).data.session
  const isAuthenticated = !!session?.access_token

  // 🔐 Armamos headers desde cero y solo si vienen como objeto plano los mergeamos
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Si headers fue provisto y es un objeto plano, mergear
  if (
    options.headers &&
    typeof options.headers === 'object' &&
    !Array.isArray(options.headers) &&
    !(options.headers instanceof Headers)
  ) {
    Object.assign(headers, options.headers)
  }

  // Add authentication header
  if (isAuthenticated && session?.access_token) {
    // Use Supabase session token for authenticated requests
    headers['Authorization'] = `Bearer ${session.access_token}`
  } else {
    // Add API key for unauthenticated requests
    const apiKey = import.meta.env.VITE_API_KEY
    if (!apiKey) {
      console.warn('[apiFetch] Missing VITE_API_KEY in env')
    } else {
      headers['Authorization'] = `Bearer ${apiKey}`
    }
  }

  let res: Response | undefined

  try {
    // VOY-36: Use fetchWithRetry with abort support
    // Generate unique abort key to prevent conflicts when same endpoint is called multiple times
    const { abortKey, suppressErrorToast, ...fetchOptions } = options;
    // Use crypto.randomUUID() if available for better uniqueness, otherwise fallback to timestamp + random
    const uniqueAbortKey = abortKey || (
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? `api-${path}-${crypto.randomUUID()}`
        : `api-${path}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}-${performance.now().toString(36)}`
    );
    res = await fetchWithRetry(
      `${API_URL}${path}`,
      {
        ...fetchOptions,
        headers,
        credentials: 'include',
        abortKey: uniqueAbortKey,
    }
    );

    if (res.status === 429) {
      toast.warning('Límite alcanzado', {
        description: 'Alcanzaste el límite de uso permitido. Intenta más tarde.',
      })
      throw new Error('Too many requests')
    }

    if (!res.ok) {
      const error = await res.text()
      if (!suppressErrorToast) {
        toast.error('Error en la solicitud', {
          description: error || 'Algo salió mal',
        })
      }
      throw new Error(error || 'Request failed')
    }

    return res
  } catch (err) {
    // Only show network error toast if it's a real network error (no response)
    // Don't show for AbortError (cancelled requests) or if response exists
    if (!res && err instanceof Error && err.name !== 'AbortError') {
      toast.error('Error de red', {
        description: 'No se pudo conectar con el servidor',
      })
    }
    // Re-throw AbortError without modification - component will handle it
    throw err
  }
}
